import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { API_BASE } from '../client/config';
import { useWriteGate } from '../nav/useWriteGate';
import { ModalShell } from './menu';
import { Button } from './ui';
import { toast } from './toast';

type Destination = {
  origin: string | null; updatedAt: string | null; canEdit: boolean; apkDistributionOrigin: string;
  devices: Array<{ id: string; name: string; groupLabel: string | null; currentOrigin: string | null;
    appVersion: string | null; supportsServerChange: boolean }>;
  commands: Array<{ id: string; deviceId: string; deviceName: string; status: string; destinationOrigin: string | null;
    createdAt: string; finishedAt: string | null; error: string | null }>;
};
const STATUS: Record<string, string> = { pending: '수신 대기', received: '전환 확인 중', success: '완료', failed: '실패', expired: '요청 만료' };
const CODE = /^BRKL(?:-[A-Z0-9]{4}){3}$/;
const date = (value: string | null) => value ? new Date(value).toLocaleString('ko-KR', { hourCycle: 'h23' }) : '—';

function normalize(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password && !url.search && !url.hash && url.pathname === '/'
      && !/[\\\s]/.test(value) && url.port !== '0' && value.length <= 256 ? url.origin : null;
  } catch { return null; }
}
async function read<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => null) as { detail?: string; error?: string } | null;
  if (!response.ok) throw new Error(body?.detail ?? (response.status === 403 ? '운영사만 목적지 서버를 관리할 수 있습니다.'
    : response.status === 409 ? '설정이나 기기 상태가 바뀌었습니다. 다시 불러온 뒤 확인해 주세요.' : '요청을 처리하지 못했습니다. 다시 시도해 주세요.'));
  if (!body) throw new Error('서버 응답을 확인할 수 없습니다.');
  return body as T;
}

/** BO tenant detail only. Saving an address never enqueues a device command. */
export function OrgPlayerServerCard({ orgId }: { orgId: string }): JSX.Element {
  const id = useId(); const write = useWriteGate();
  const endpoint = `${API_BASE}/api/bo/tenants/${encodeURIComponent(orgId)}/player-server`;
  const [data, setData] = useState<Destination | null>(null);
  const [draft, setDraft] = useState(''); const [baseline, setBaseline] = useState<string | null>(null);
  const [loading, setLoading] = useState(true); const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false); const [deviceId, setDeviceId] = useState('');
  const [code, setCode] = useState(''); const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const attempt = useRef<{ signature: string; key: string } | null>(null);

  const load = useCallback(async (resetDraft: boolean, signal?: AbortSignal) => {
    setLoading(true); setError(null);
    try {
      const next = await read<Destination>(await fetch(endpoint, signal ? { signal } : {}));
      if (signal?.aborted) return;
      setData(next);
      if (resetDraft) { setDraft(next.origin ?? ''); setBaseline(next.origin); }
    } catch (e) { if (!signal?.aborted) setError(e instanceof Error ? e.message : '설정을 불러오지 못했습니다.'); }
    finally { if (!signal?.aborted) setLoading(false); }
  }, [endpoint]);
  useEffect(() => { const controller = new AbortController(); void load(true, controller.signal); return () => controller.abort(); }, [load]);

  const input = draft.trim(); const normalized = input ? normalize(input) : null;
  const invalid = Boolean(input && !normalized);
  const dirty = !invalid && normalized !== baseline;
  const editable = !!data?.canEdit && !write.blocked && !error;
  const selected = data?.devices.find(device => device.id === deviceId);
  const canMove = editable && !!data?.origin && !dirty && !invalid && normalized === data.origin;
  const save = async () => {
    if (!editable || saving || invalid || !dirty) return;
    setSaving(true); setError(null);
    try {
      const saved = await read<{ origin: string | null; updatedAt: string | null }>(await fetch(endpoint, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ origin: normalized, expectedOrigin: baseline }),
      }));
      setData(previous => previous ? { ...previous, ...saved } : previous);
      setDraft(saved.origin ?? ''); setBaseline(saved.origin);
      toast('목적지 서버 주소를 저장했습니다. 기기 연결은 변경되지 않았습니다.', 'success');
    } catch (e) { setError(e instanceof Error ? e.message : '저장하지 못했습니다.'); }
    finally { setSaving(false); }
  };
  const requestMove = async () => {
    if (!canMove || !selected?.supportsServerChange || selected.currentOrigin === data?.origin || !CODE.test(code.trim()) || sending) return;
    setSending(true); setSendError(null);
    const signature = JSON.stringify([orgId, deviceId, data!.origin, code.trim()]);
    if (attempt.current?.signature !== signature) attempt.current = { signature, key: crypto.randomUUID() };
    try {
      await read(await fetch(`${API_BASE}/api/devices/${encodeURIComponent(deviceId)}/commands`, { method: 'POST',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ command: 'setServerOrigin', idempotencyKey: attempt.current.key,
          payload: { origin: data!.origin, pairingCode: code.trim() } }) }));
      setOpen(false); setCode('');
      toast('기기에 서버 변경을 요청했습니다. 최근 요청에서 처리 결과를 확인해 주세요.', 'success');
      await load(false);
    } catch (e) { setSendError(e instanceof Error ? e.message : '변경을 요청하지 못했습니다.'); }
    finally { setSending(false); }
  };

  return <section className="border border-line rounded-md bg-panel mb-6" aria-labelledby={`${id}-title`}>
    <div className="px-5 py-3 border-b border-line flex flex-wrap items-center justify-between gap-3">
      <h2 id={`${id}-title`} className="text-[15px] font-semibold">플레이어 목적지 서버 설정</h2>
      <Button disabled={loading || saving || sending} onClick={() => void load(true)}>다시 불러오기</Button>
    </div>
    <div className="px-5 py-4 space-y-4">
      {error && <p role="alert" className="text-[13px] text-red">{error}</p>}
      {loading && !data && <p role="status" className="text-[13px] text-text-dim">서버 설정을 불러오는 중입니다.</p>}
      {data && <>
        <div className="flex flex-col gap-2">
          <label htmlFor={`${id}-origin`} className="text-[13px] font-medium">목적지 서버 주소</label>
          <div className="flex flex-wrap items-center gap-2">
            <input id={`${id}-origin`} type="url" autoComplete="off" spellCheck={false} placeholder="https://stationc.bricklit.club"
              value={draft} disabled={!editable || saving || loading} onChange={event => setDraft(event.target.value)}
              aria-invalid={invalid} aria-describedby={`${id}-help`}
              className="min-w-0 w-full sm:w-[min(100%,560px)] px-3 py-2 border border-line rounded bg-panel-2 text-[14px] text-text disabled:opacity-60" />
            <Button variant="primary" disabled={!editable || !dirty || invalid || loading || saving} title={write.reason} onClick={() => void save()}>
              {saving ? '저장 중…' : '주소 저장'}
            </Button>
          </div>
          <p id={`${id}-help`} className={`text-[12px] ${invalid ? 'text-red' : 'text-text-dim'}`}>
            {invalid ? '경로·계정·쿼리가 없는 HTTPS 주소를 입력해 주세요.' : '주소 저장만으로 기기가 이동하지 않습니다. 저장 후 변경할 기기를 선택해 주세요.'}
          </p>
          <p className="text-[12px] text-text-dim">{data.origin ? `마지막 저장 ${date(data.updatedAt)}` : '아직 목적지 서버가 설정되지 않았습니다.'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 border-t border-line pt-4 text-[13px]">
          <div><span className="text-text-dim">APK 배포 서버</span><span className="ml-3 break-all">{data.apkDistributionOrigin}</span></div>
          <p className="text-[12px] text-text-dim">조직 서버가 달라져도 공통 APK를 사용합니다.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button disabled={!canMove || loading || saving || !data.devices.some(device => device.supportsServerChange && device.currentOrigin !== data.origin)}
            title={write.reason} onClick={() => { setDeviceId(''); setCode(''); setSendError(null); attempt.current = null; setOpen(true); }}>기기 서버 변경</Button>
          <span className="text-[12px] text-text-dim">원격 변경을 지원하는 APK 0.5.8 이상 기기만 선택할 수 있습니다.</span>
        </div>
        {!data.canEdit && <p className="text-[12px] text-text-dim">삭제 예정이거나 정지된 조직은 설정을 변경할 수 없습니다.</p>}
        {data.commands.length > 0 && <div className="overflow-x-auto border border-line rounded">
          <table className="w-full text-left text-[12px]" aria-label="최근 서버 변경 요청">
            <thead className="bg-panel-2 text-text-dim"><tr><th className="px-3 py-2 font-medium">기기</th><th className="px-3 py-2 font-medium">목적지</th><th className="px-3 py-2 font-medium">처리 상태</th><th className="px-3 py-2 font-medium">요청 시각</th></tr></thead>
            <tbody>{data.commands.map(command => <tr key={command.id} className="border-t border-line">
              <td className="px-3 py-2">{command.deviceName}</td><td className="px-3 py-2 break-all">{command.destinationOrigin ?? '—'}</td>
              <td className="px-3 py-2"><span>{STATUS[command.status] ?? '확인 필요'}</span>{command.status === 'failed' && <p className="mt-1 text-text-dim">연결·등록 상태를 확인하고 새 등록코드로 다시 요청해 주세요.</p>}</td>
              <td className="px-3 py-2 whitespace-nowrap">{date(command.createdAt)}</td>
            </tr>)}</tbody>
          </table>
          <p className="px-3 py-2 text-[12px] text-text-dim">‘전환 확인 중’은 완료가 아닙니다. ‘다시 불러오기’로 최신 결과를 확인해 주세요.</p>
        </div>}
      </>}
    </div>
    {open && data && <ModalShell title="기기 서버 변경" size="md" onClose={() => { if (!sending) { setOpen(false); setCode(''); } }}
      footer={<div className="flex justify-end gap-2"><Button disabled={sending} onClick={() => { setOpen(false); setCode(''); }}>취소</Button>
        <Button variant="primary" disabled={!canMove || !selected?.supportsServerChange || selected.currentOrigin === data.origin || !CODE.test(code.trim()) || sending}
          onClick={() => void requestMove()}>{sending ? '요청 중…' : '선택 기기 서버 변경'}</Button></div>}>
      <div className="space-y-4">
        <p className="text-[13px]">목적지 <strong className="break-all">{data.origin}</strong></p>
        <label className="flex flex-col gap-2 text-[13px]">변경할 기기
          <select value={deviceId} onChange={event => setDeviceId(event.target.value)} disabled={sending} className="w-full border border-line rounded bg-panel-2 px-3 py-2">
            <option value="">기기 선택</option>{data.devices.map(device => <option key={device.id} value={device.id}
              disabled={!device.supportsServerChange || device.currentOrigin === data.origin}>{device.name}{device.groupLabel ? ` · ${device.groupLabel}` : ''}
              {!device.supportsServerChange ? ' · APK 업데이트 필요' : device.currentOrigin === data.origin ? ' · 같은 서버에 연결됨' : ''}</option>)}
          </select>
        </label>
        {selected && <p className="text-[12px] text-text-dim">현재 서버: {selected.currentOrigin ?? '확인 필요'} · APK {selected.appVersion ?? '확인 필요'}</p>}
        <label className="flex flex-col gap-2 text-[13px]">목적지 서버의 새 기기 등록코드
          <input value={code} onChange={event => setCode(event.target.value.toUpperCase())} disabled={sending} autoComplete="off" spellCheck={false}
            placeholder="BRKL-XXXX-XXXX-XXXX" className="w-full border border-line rounded bg-panel-2 px-3 py-2 font-mono" />
        </label>
        <p className="text-[12px] text-text-dim">목적지 서버에서 원하는 조직·그룹의 새 등록코드를 발급해 입력해 주세요. 기존 기기 갱신코드는 사용할 수 없습니다. 요청 후 10분 안에 기기가 연결돼야 합니다.</p>
        <p className="text-[12px] text-text-dim">새 서버 연결·인증을 확인한 뒤 전환하며, 실패하면 기존 주소를 유지하거나 복귀합니다. 조직·콘텐츠 데이터는 별도로 준비해야 합니다.</p>
        {sendError && <p role="alert" className="text-[13px] text-red">{sendError}</p>}
      </div>
    </ModalShell>}
  </section>;
}
